import { useCallback, useSyncExternalStore } from 'react'
import type { OrbKind } from '@/components/Orb/Orb'
import { useSSE } from '../contexts/SSEContext'
import { SSE_EVENTS, isBotMessageEvent } from '../contexts/SSEEvents'

/**
 * Which strategies have a long job live right now, and what it is: the sidebar's running orb
 * (D-p3-687), shown wherever the trader has navigated to, wearing the same orb as the chat.
 *
 * Fed by the chat's own live events, which reach every open page. A strategy lights up once
 * one of the long jobs starts on it, and stays lit until its AI turn has nothing left in
 * flight. Plain chat replies never light it, and neither does a question to the trader: the
 * questions tool turns into a form and waits, so `bot-message-type-changed` ends its row
 * (no done event follows until the trader answers).
 *
 * A page loaded mid-job is seeded from the strategy list (`runningJobs`: the job rows the
 * server still has in progress), and each seeded job ends on its own row's done event like a
 * live one.
 */

/**
 * The long jobs, each with the orb its chat card runs (BotMessageToolGenerateCode,
 * BotMessageToolWalkthrough, the backtest card's default, BotMessageToolOptimizer). Anything
 * else (questions, quick tools) does not light the orb.
 */
const JOB_ORBS: Readonly<Record<string, OrbKind>> = {
  generate_trading_code: 'generating',
  run_walkthrough: 'walkthrough',
  run_backtest: 'running',
  run_optimizer: 'searching',
}

/**
 * Bridges the hand-off inside one turn: a tool ends and the next stream starts a moment
 * later. Without it the orb would blink off between the steps of an agentic run.
 */
export const RUNNING_GRACE_MS = 1500

const ENDS: ReadonlySet<string> = new Set([
  SSE_EVENTS.BOT_MESSAGE_DONE,
  SSE_EVENTS.BOT_MESSAGE_FAILED,
  SSE_EVENTS.BOT_MESSAGE_CANCELED,
  SSE_EVENTS.BOT_MESSAGE_DELETED,
  SSE_EVENTS.BOT_MESSAGE_TOOL_DONE,
  SSE_EVENTS.BOT_MESSAGE_TYPE_CHANGED,
])

interface Activity {
  /** Rows of the current turn still in progress (assistant streams and tool calls). */
  inFlight: Set<string>
  /** What the orb shows; set once a long job has started in this turn. */
  orb?: OrbKind
  /** Pending end of the turn, while the grace runs. */
  timer?: ReturnType<typeof setTimeout>
}

const activity = new Map<string, Activity>()
/**
 * Rows already seen ending. The strategy list can be in flight while a job finishes; seeding
 * from it afterwards must not light a job that is already over. Bounded: only recent ends
 * can race a list request.
 */
const ended = new Set<string>()
const ENDED_KEEP = 200
let running: ReadonlyMap<string, OrbKind> = new Map()
const listeners = new Set<() => void>()

function publish() {
  const next = new Map<string, OrbKind>()
  activity.forEach((a, bid) => a.orb && next.set(bid, a.orb))
  if (next.size === running.size && [...next].every(([bid, orb]) => running.get(bid) === orb)) return
  running = next
  listeners.forEach((l) => l())
}

/** A row starts. `orb` is the job's orb, or null for an assistant stream (the AI thinking). */
function start(bid: string, msgId: string, orb: OrbKind | null) {
  const a = activity.get(bid) ?? { inFlight: new Set<string>() }
  clearTimeout(a.timer)
  a.timer = undefined
  a.inFlight.add(msgId)
  // Once a job has run in this turn, the AI's streams between and after its steps show as
  // thinking, as in the chat; before any job, a stream lights nothing.
  if (orb) a.orb = orb
  else if (a.orb) a.orb = 'thinking'
  activity.set(bid, a)
  publish()
}

function end(bid: string, msgId: string) {
  ended.add(msgId)
  if (ended.size > ENDED_KEEP) ended.delete(ended.values().next().value as string)
  const a = activity.get(bid)
  if (!a) return
  a.inFlight.delete(msgId)
  if (a.inFlight.size || a.timer) return
  a.timer = setTimeout(() => {
    if (activity.get(bid) !== a || a.inFlight.size) return
    activity.delete(bid)
    publish()
  }, RUNNING_GRACE_MS)
}

/** Reads one chat event; anything without a strategy and a row is ignored. */
export function trackRunningJob(message: { event?: string; botId?: string; msgId?: string | number; toolName?: string }) {
  const { event, botId, msgId } = message ?? {}
  if (!event || !botId || msgId === undefined || msgId === null) return
  const id = String(msgId)
  if (event === SSE_EVENTS.BOT_MESSAGE_STARTED) start(botId, id, null)
  else if (event === SSE_EVENTS.BOT_MESSAGE_TOOL_STARTED) {
    const orb = JOB_ORBS[message.toolName ?? '']
    // Not a long job: its row still counts toward the turn, but it lights nothing.
    if (orb) start(botId, id, orb)
    else if (activity.has(botId)) activity.get(botId)!.inFlight.add(id)
  } else if (ENDS.has(event)) end(botId, id)
}

/** Seeds the jobs a freshly loaded strategy list says are still running. */
export function seedRunningJobs(bots: Array<{ bid: string; runningJobs?: Array<{ msgId: string | number; tool: string }> }>) {
  for (const { bid, runningJobs } of bots) {
    for (const { msgId, tool } of runningJobs ?? []) {
      const id = String(msgId)
      const orb = JOB_ORBS[tool]
      if (orb && !ended.has(id)) start(bid, id, orb)
    }
  }
}

export const runningStrategiesStore = {
  get: () => running,
  subscribe(listener: () => void) {
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  },
  /** Test seam: forget every turn. */
  reset() {
    activity.forEach((a) => clearTimeout(a.timer))
    activity.clear()
    ended.clear()
    running = new Map()
    listeners.forEach((l) => l())
  },
}

/** The strategies (bids) with a long job live, each with the orb to show. */
export function useRunningStrategies() {
  return useSyncExternalStore(runningStrategiesStore.subscribe, runningStrategiesStore.get)
}

/** Feeds the store from the app-wide event stream; mounted once, by the sidebar. */
export function useTrackRunningStrategies() {
  useSSE(
    useCallback((msg: { event?: string }) => isBotMessageEvent(msg.event), []),
    trackRunningJob,
  )
}
